/**
 * The approval state of one process, as an icon that tells the truth.
 *
 * Every process card in the MBMR tree used to render the same `ShieldCheck` — a shield with a
 * tick — whatever the process's real state was, because the icon was a *button* ("Send for
 * Approval") that happened to look like a verdict. On a draft recipe like Enoxaparin, whose
 * five processes have never been submitted, five ticks read as "fully approved" while the
 * header two rows above said "Process Approvals: Not Provided". A tick beats a dot, and it
 * beat the readiness dot next to it.
 *
 * Four states, four icons, and never colour or glyph alone: each carries a `title` and an
 * `aria-label` naming the state in words.
 *
 *   draft / never submitted  →  plain outline shield   "Approval: not yet submitted"
 *   under review             →  shield + ellipsis      "Approval: under review"
 *   returned                 →  shield + alert         "Returned for correction — see reason"
 *   approved                 →  shield + tick          "Approved"
 *
 * The state comes from the readiness payload's `lifecycle.processes[]`, NOT from
 * `process_status` on `get_process_operation_by_recipe` — that field is an empty string for
 * every process on both a fully approved recipe and a draft one, so it carries no signal.
 */
import React from 'react';
import { Tooltip } from 'antd';
import { Shield, ShieldAlert, ShieldCheck, ShieldEllipsis } from 'lucide-react';
import { useTheme } from '@/App';
import { LIFECYCLE, lifecycleColors, safeLifecycle } from './readinessModel';

const ICONS = {
  draft: Shield,
  partially_submitted: ShieldEllipsis,
  under_review: ShieldEllipsis,
  returned: ShieldAlert,
  approved: ShieldCheck,
};

const TITLES = {
  draft: 'Approval: not yet submitted',
  partially_submitted: 'Approval: partly under review',
  under_review: 'Approval: under review',
  returned: 'Returned for correction — see the reason',
  approved: 'Approved',
};

/** The full sentence for a tooltip: state, plus who returned it and why when that applies. */
export const approvalTitle = (row) => {
  const state = safeLifecycle(row?.state);
  if (state === 'returned' && row?.returned) {
    const when = row.returned.at ? new Date(row.returned.at).toLocaleString() : '';
    const who = row.returned.by || 'a reviewer';
    return `Returned for correction by ${who}${when ? ` on ${when}` : ''} — ${row.returned.reason || 'no reason recorded'}`;
  }
  if (state === 'approved' && row?.status) return `${row.status} — approved`;
  return TITLES[state];
};

/**
 * @param {{row: object|null, size?: number, onClick?: function, actionable?: boolean,
 *          actionTitle?: string, selected?: boolean}} props
 * `row` is one entry of `lifecycle.processes[]`. `actionable` keeps the icon's old job — a
 * click opens the approval-roles modal — but only where sending is a legal next step; in
 * every other state the icon is a read-out and says so by not offering a pointer.
 */
export default function ApprovalStateIcon({
  row,
  size = 18,
  onClick,
  actionable = false,
  actionTitle = '',
  selected = false,
}) {
  const { isDark } = useTheme();
  const state = safeLifecycle(row?.state);
  const Icon = ICONS[state] || Shield;
  const { fg } = lifecycleColors(state, isDark);
  const label = approvalTitle(row);
  const title = actionable && actionTitle ? `${label} · ${actionTitle}` : label;

  return (
    <Tooltip title={title}>
      <Icon
        role="img"
        aria-label={`Approval: ${LIFECYCLE[state].label}`}
        title={title}
        style={{
          width: size,
          height: size,
          // A selected (inverted) card paints its own contrast; everywhere else the tone
          // colour is what distinguishes the four states at a glance.
          color: selected ? '#ffffff' : fg,
          cursor: actionable ? 'pointer' : 'default',
          flex: 'none',
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (actionable) onClick?.(e);
        }}
      />
    </Tooltip>
  );
}

/** The same thing as a text pill, for the rail header where there is room for words. */
export function ApprovalStatePill({ state, children }) {
  const { isDark } = useTheme();
  const key = safeLifecycle(state);
  const { fg, bg } = lifecycleColors(key, isDark);
  const Icon = ICONS[key] || Shield;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5"
      style={{ fontSize: 11, fontWeight: 600, color: fg, backgroundColor: bg }}
    >
      <Icon style={{ width: 12, height: 12, flex: 'none' }} aria-hidden="true" />
      {children || LIFECYCLE[key].label}
    </span>
  );
}
